import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ApiService } from "../api/api-service";
import {
  BatchSession,
  BatchTableRow,
  WindowReport,
} from "./types/batchSession.types";
import AutomationReport from "./components/AutomationReport";
import DownloadReport from "./components/DownloadReport";
import BatchesTable from "./components/BatchesSessionTable";
import {
  RecordingToastContainer,
  RecordingEvent,
} from "./components/RecordingToast";
import {
  StatusPill,
  SessionStatusPill,
  RecordingIndicator,
  formatDate,
} from "./components/Pills";

/* Batch timing lives on the instructor entry of the CURRENT trainer (same
   lookup as BatchForm); fall back to the first entry. */
const getBatchTiming = (b: any): string => {
  const list: any[] = b?.instructor || [];
  if (list.length === 0) return "";
  const email = (b.trainerEmail || "").toLowerCase().trim();
  const match = email
    ? list.find((i) => (i?.email || "").toLowerCase().trim() === email)
    : undefined;
  return (match ?? list[0])?.batchTiming || "";
};

const api = ApiService.getInstance();
const PAGE_SIZE = 15;
const REPORT_WINDOW_HOURS = 12;
const REPORT_REFRESH_MS = 30000;

/* ── polling tuning ──────────────────────────────────────────────────────── */
const POLL_INTERVAL_MS = 3000; // same near-real-time cadence as before
const POLL_JITTER_MS = 400; // ± spread so concurrent admin tabs don't sync up
const POLL_MAX_BACKOFF_MS = 60000; // cap after repeated failures

/* ── helpers ─────────────────────────────────────────────────────────────── */
function getAdminName(): string {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const u = JSON.parse(raw);
    return `${u.firstName || ""} ${u.lastName || ""}`.trim();
  } catch {
    return "";
  }
}

/**
 * Parse a time string like "4:00", "16:30", "22:08" into total minutes from midnight.
 * Returns -1 if the string is not parseable.
 */
function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) return -1;
  const cleaned = timeStr.trim();
  const match = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return -1;
  const hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return -1;
  return hours * 60 + mins;
}

/** Get current time in minutes from midnight (IST) */
function getCurrentTimeMinutes(): number {
  const now = new Date();
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60; // minutes
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const istMinutes = (utcMinutes + istOffset) % (24 * 60);
  return istMinutes;
}

/** Get today's date string in YYYY-MM-DD (IST) */
function getTodayIST(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().split("T")[0];
}

/**
 * Merge a delta batch of sessions into the current list by sessionId.
 * The backend never deletes session rows, so a plain upsert (insert new,
 * replace changed) is always correct — nothing needs to be removed.
 */
function upsertSessions(
  prev: BatchSession[],
  incoming: BatchSession[],
): BatchSession[] {
  if (incoming.length === 0) return prev;
  const map = new Map(prev.map((s) => [s.sessionId, s]));
  incoming.forEach((s) => map.set(s.sessionId, s));
  return Array.from(map.values());
}

/** Latest lastUpdated (falls back to createdDate) across a list of sessions. */
function getMaxLastUpdated(list: BatchSession[]): string {
  let max = "";
  list.forEach((s) => {
    const v = s.lastUpdated || s.createdDate || "";
    if (v && v > max) max = v;
  });
  return max;
}

/** Poll delay with small random jitter, so many open tabs don't poll in lockstep. */
function jitteredDelay(baseMs: number): number {
  const jitter = Math.floor((Math.random() * 2 - 1) * POLL_JITTER_MS);
  return Math.max(500, baseMs + jitter);
}

/**
 * The backend /batch-sessions endpoint transforms flat fields (trainerName,
 * trainerEmail, trainerStatus, trainerClickedAt, trainerJoinedAt) into a
 * nested trainer object. But we also handle the raw flat format as a fallback.
 */
function resolveTrainer(s: any) {
  if (s.trainer && s.trainer.name) return s.trainer;
  if (s.trainerName || s.trainerEmail) {
    return {
      name: s.trainerName || "",
      email: s.trainerEmail || "",
      status: s.trainerStatus || "pending",
      clickedAt: s.trainerClickedAt || null,
      joinedAt: s.trainerJoinedAt || null,
      attendedAt: s.trainerJoinedAt || null,
    };
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function BatchSessions() {
  const navigate = useNavigate();

  /* ── auth ───────────────────────────────────────────────────────────── */
  const [loggedInAdmin, setLoggedInAdmin] = useState("");

  /* ── data ───────────────────────────────────────────────────────────── */
  const [sessions, setSessions] = useState<BatchSession[]>([]);
  const [batchesRaw, setBatchesRaw] = useState<any[]>([]); // raw from /batches
  const [allAdmins, setAllAdmins] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /* ── view mode ──────────────────────────────────────────────────────── */
  const [viewMode, setViewMode] = useState<"batches" | "sessions">("batches");
  const [selectedBatchNo, setSelectedBatchNo] = useState<string | null>(null);

  /* ── filters ────────────────────────────────────────────────────────── */
  const [searchBatch, setSearchBatch] = useState("");
  const [batchTimingFilter, setBatchTimingFilter] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState(""); // "" means "use logged-in admin"

  /* ── pagination ─────────────────────────────────────────────────────── */
  const [batchPage, setBatchPage] = useState(1);
  const [sessionPage, setSessionPage] = useState(1);

  /* ── automation report (server-computed: header counters + report panel) ── */
  const [report, setReport] = useState<WindowReport | null>(null);
  const [reportError, setReportError] = useState(false);

  /* ── detail modal ───────────────────────────────────────────────────── */
  const [detailModal, setDetailModal] = useState<BatchSession | null>(null);

  const [recToasts, setRecToasts] = useState<RecordingEvent[]>([]);
  const prevSessionsRef = useRef<BatchSession[]>([]);

  /* ── polling/fetch robustness refs ─────────────────────────────────── */
  const isFetchingSessionsRef = useRef(false); // overlap guard for routine poll ticks
  const sessionAbortControllerRef = useRef<AbortController | null>(null);
  const sessionRequestSeqRef = useRef(0); // discards stale out-of-order responses
  const lastUpdatedCursorRef = useRef<string>(""); // delta-fetch watermark
  const pollConsecutiveErrorsRef = useRef(0); // drives exponential backoff
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  /**
   * Fetch /batch-sessions, robustly.
   *
   * - mode "full": no updatedAfter param — full data (initial load, manual
   *   refresh). Replaces `sessions` outright and (re)seeds the delta cursor.
   * - mode "delta": sends updatedAfter=<cursor> once a cursor exists (falls
   *   back to a full fetch automatically if it doesn't yet). Upserts the
   *   result into `sessions` instead of replacing it, since a delta response
   *   only contains what changed.
   *
   * Routine poll ticks (force=false) skip themselves if a request is already
   * in flight — never overlapping. An explicit refresh (force=true) instead
   * aborts whatever's in flight and supersedes it. A request-sequence guard
   * drops any response that's no longer the latest one issued, so a slow
   * poll response can't clobber a newer refresh (or vice versa).
   */
  const fetchSessions = useCallback(
    async (
      mode: "full" | "delta",
      opts?: { force?: boolean },
    ): Promise<{ ok: boolean; skipped?: boolean; aborted?: boolean }> => {
      const force = opts?.force ?? false;

      if (isFetchingSessionsRef.current) {
        if (!force) return { ok: false, skipped: true };
        sessionAbortControllerRef.current?.abort();
      }

      const controller = new AbortController();
      sessionAbortControllerRef.current = controller;
      const seq = ++sessionRequestSeqRef.current;
      isFetchingSessionsRef.current = true;

      const cursor = lastUpdatedCursorRef.current;
      const useDelta = mode === "delta" && !!cursor;

      try {
        const res = await api.get("/batch-sessions", {
          params: useDelta ? { updatedAfter: cursor } : undefined,
          signal: controller.signal,
        });

        // A newer request has since been issued — drop this stale response
        // rather than let it overwrite fresher data.
        if (seq !== sessionRequestSeqRef.current || !mountedRef.current) {
          return { ok: true };
        }

        const freshSessions: BatchSession[] = res.data?.data || [];
        const responseMode = res.data?.meta?.mode === "delta" ? "delta" : "full";

        setSessions((prev) =>
          responseMode === "delta"
            ? upsertSessions(prev, freshSessions)
            : freshSessions,
        );

        const maxUpdated = getMaxLastUpdated(freshSessions);
        if (maxUpdated && maxUpdated > lastUpdatedCursorRef.current) {
          lastUpdatedCursorRef.current = maxUpdated;
        }

        pollConsecutiveErrorsRef.current = 0;
        return { ok: true };
      } catch (err) {
        if (axios.isCancel(err)) {
          // Expected: aborted by a newer request, unmount, or a hidden tab —
          // not a real failure, don't count it toward backoff.
          return { ok: false, aborted: true };
        }
        // Silently ignore polling errors — don't disrupt UX
        console.error("[BatchSessions] Session fetch error:", err);
        pollConsecutiveErrorsRef.current += 1;
        return { ok: false };
      } finally {
        // Only the still-latest request may clear the in-flight flag — an
        // aborted older call finishing later must not mark us as idle while
        // the request that superseded it is still running.
        if (seq === sessionRequestSeqRef.current) {
          isFetchingSessionsRef.current = false;
        }
      }
    },
    [],
  );

  /* ── fetch ──────────────────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.allSettled([
        fetchSessions("full", { force: true }),
        (async () => {
          const batchesRes = await api.get("/batches");
          setBatchesRaw(batchesRes.data?.data || []);
        })(),
        (async () => {
          const adminsRes = await api.get("/admin");
          const adminData =
            adminsRes.data?.data ||
            adminsRes.data?.admins || // handles { admins: [...] }
            adminsRes.data ||
            [];

          const names: string[] = adminData
            .map((a: any) => {
              if (typeof a === "string") return a;
              // Your API shape: { firstName, lastName, adminId, email }
              return `${a.firstName || ""} ${a.lastName || ""}`.trim();
            })
            .filter(Boolean);

          const uniqueNames = [...new Set(names)];
          const currentAdmin = getAdminName();
          const finalAdmins = uniqueNames.includes(currentAdmin)
            ? uniqueNames
            : [currentAdmin, ...uniqueNames].filter(Boolean);

          setAllAdmins(finalAdmins);
        })(),
      ]);
    } catch (err) {
      console.error("[BatchSessions] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchSessions]);

  useEffect(() => {
    const name = getAdminName();
    setLoggedInAdmin(name);
    setAdminFilter(name); // default to logged-in admin
    fetchData();
  }, [fetchData]);

  /* ── Robust polling: delta-fetch /batch-sessions on a ~3s cadence ────
   * - jittered interval (no lockstep across admin tabs)
   * - paused entirely while the tab is hidden, resumes immediately on refocus
   * - never overlaps a routine tick with an in-flight request
   * - exponential backoff (capped) after consecutive failures
   * - aborts its in-flight request on unmount
   */
  useEffect(() => {
    mountedRef.current = true;

    const runPoll = async () => {
      if (document.hidden) {
        // Fully stop — handleVisibilityChange restarts this when refocused.
        pollTimeoutRef.current = null;
        return;
      }

      const result = await fetchSessions("delta");
      if (!mountedRef.current) return;

      const delay =
        result.ok || result.skipped || result.aborted
          ? jitteredDelay(POLL_INTERVAL_MS)
          : jitteredDelay(
              Math.min(
                POLL_INTERVAL_MS * 2 ** pollConsecutiveErrorsRef.current,
                POLL_MAX_BACKOFF_MS,
              ),
            );

      pollTimeoutRef.current = setTimeout(runPoll, delay);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else if (!pollTimeoutRef.current) {
        // Loop had been stopped while hidden — refresh right away.
        runPoll();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    pollTimeoutRef.current = setTimeout(runPoll, jitteredDelay(POLL_INTERVAL_MS));

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      sessionAbortControllerRef.current?.abort();
    };
  }, [fetchSessions]);
  useEffect(() => {
    const prev = prevSessionsRef.current;
    if (prev.length === 0) {
      prevSessionsRef.current = sessions;
      return;
    }

    const newToasts: RecordingEvent[] = [];

    sessions.forEach((curr) => {
      const old = prev.find((p) => p.sessionId === curr.sessionId);
      if (!old) return;

      const oldRec = (old.recordingStatus || "").toLowerCase();
      const newRec = (curr.recordingStatus || "").toLowerCase();

      // Recording just started (was not recording before, now recording)
      if (oldRec !== "recording" && newRec === "recording") {
        newToasts.push({
          id: `${curr.sessionId}-started-${Date.now()}`,
          batchNo: curr.batchNo,
          courseName: curr.courseName,
          type: "started",
          timestamp: Date.now(),
        });
      }

      // Recording was active before but now stopped
      if (
        oldRec === "recording" &&
        (newRec === "processing" || newRec === "available" || newRec === "none")
      ) {
        newToasts.push({
          id: `${curr.sessionId}-stopped-${Date.now()}`,
          batchNo: curr.batchNo,
          courseName: curr.courseName,
          type: "stopped",
          timestamp: Date.now(),
        });
      }

      // Recording was recording, now paused (if your backend supports a "paused" state)
      if (oldRec === "recording" && newRec === "paused") {
        newToasts.push({
          id: `${curr.sessionId}-paused-${Date.now()}`,
          batchNo: curr.batchNo,
          courseName: curr.courseName,
          type: "paused",
          timestamp: Date.now(),
        });
      }
    });

    if (newToasts.length > 0) {
      setRecToasts((prev) => [...prev, ...newToasts]);
    }

    prevSessionsRef.current = sessions;
  }, [sessions]);

  const dismissToast = useCallback((id: string) => {
    setRecToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchReport()]);
    setTimeout(() => setRefreshing(false), 600);
  };

  /* ── effective admin for filtering ─────────────────────────────────── */
  const effectiveAdmin = adminFilter || loggedInAdmin;

  /* ── unique batch timings from batches data ─────────────────────────── */
  const allTimings = useMemo(() => {
    const timings = new Set<string>();
    batchesRaw.forEach((b) => {
      const t = getBatchTiming(b);
      if (t) timings.add(t);
    });
    return Array.from(timings).sort();
  }, [batchesRaw]);

  /* ── sessions grouped by batchNo ───────────────────────────────────── */
  const sessionsByBatch = useMemo(() => {
    const map = new Map<string, BatchSession[]>();
    sessions.forEach((s) => {
      const existing = map.get(s.batchNo) || [];
      existing.push(s);
      map.set(s.batchNo, existing);
    });
    return map;
  }, [sessions]);

  /* ── Level 1: build BatchTableRow from /batches + session stats ──────── */
  const batchRows = useMemo((): BatchTableRow[] => {
    const todayStr = getTodayIST();
    const currentMinutes = getCurrentTimeMinutes();

    const rows = batchesRaw
      .filter((b) => {
        // Only show On-going batches in this module
        const bStatus = (b.batchStatus || "").toLowerCase();
        if (bStatus !== "on-going") return false;

        // Admin filter
        const bAdmin = (b.admin || "").toLowerCase().trim();
        if (effectiveAdmin && bAdmin !== effectiveAdmin.toLowerCase().trim()) {
          return false;
        }
        // Batch number search
        if (
          searchBatch &&
          !b.batch_no?.toLowerCase().includes(searchBatch.toLowerCase())
        ) {
          return false;
        }
        // Batch timing filter
        if (batchTimingFilter) {
          const t = getBatchTiming(b);
          if (t !== batchTimingFilter) return false;
        }
        // Schedule type filter
        if (scheduleFilter && (b.scheduleStatus || "") !== scheduleFilter) {
          return false;
        }
        return true;
      })
      .map((b) => {
        const batchSessions = sessionsByBatch.get(b.batch_no) || [];
        const liveCount = batchSessions.filter((s) => {
          const st = (s.sessionStatus || "").toLowerCase();
          return st === "live" || st === "active";
        }).length;

        // Count distinct attended students across all sessions for this batch
        const attendedEmails = new Set<string>();
        batchSessions.forEach((s) => {
          s.studentDetails.forEach((sd) => {
            const status = (sd.status || "").toLowerCase();
            if (status === "joined" || status === "attended") {
              attendedEmails.add(sd.email);
            }
          });
        });

        const students = (b.students || []).map((s: any) => ({
          name: s.name || "",
          email: s.email || "",
        }));

        // ── Find today's latest session for inline preview ──
        const todaySessions = batchSessions
          .filter((s) => {
            const created = (s.createdDate || "").split("T")[0];
            return created === todayStr;
          })
          .sort((a, b) => {
            const da = new Date(a.lastUpdated || a.createdDate || 0).getTime();
            const db = new Date(b.lastUpdated || b.createdDate || 0).getTime();
            return db - da;
          });

        const latestSession = todaySessions[0] || null;
        let latestTrainer: { name: string; status: string } | null = null;
        let latestStudents: { name: string; status: string }[] = [];
        let latestRecStatus = "none";
        let latestRecordings: any[] = [];
        let latestSessionStatus = "";
        let latestUnknown: any[] = [];

        if (latestSession) {
          const tr = resolveTrainer(latestSession);
          if (tr) {
            latestTrainer = { name: tr.name, status: tr.status };
          }
          latestStudents = (latestSession.studentDetails || []).map(
            (sd: any) => ({
              name: sd.name || "",
              status: sd.status || "pending",
            }),
          );
          latestRecStatus = latestSession.recordingStatus || "none";
          latestRecordings = latestSession.recordings || [];
          latestUnknown = latestSession.unknownParticipants || [];
          latestSessionStatus = latestSession.sessionStatus || "";
        }

        return {
          batchNo: b.batch_no || "",
          courseName: b.courseName || "",
          batchTiming: getBatchTiming(b),
          trainerName: b.trainerName || "",
          admin: b.admin || "",
          meetingLink: b.meetingLink || "",
          scheduleStatus: b.scheduleStatus || "",
          scheduleDetails: b.scheduleDetails || "",
          students,
          attendedCount: attendedEmails.size,
          totalStudents: students.length,
          sessionCount: batchSessions.length,
          liveSessionCount: liveCount,
          batchStatus: b.batchStatus || "Not Started",
          latestSessionTrainer: latestTrainer,
          latestSessionStudents: latestStudents,
          latestSessionRecordingStatus: latestRecStatus,
          latestSessionRecordings: latestRecordings,
          latestSessionUnknownParticipants: latestUnknown,
          latestSessionStatus: latestSessionStatus,
        };
      });

    // ── Proximity sort: nearest batch timing to current time first ──
    rows.sort((a, b) => {
      const aMin = parseTimeToMinutes(a.batchTiming);
      const bMin = parseTimeToMinutes(b.batchTiming);

      // Batches with no timing go to the bottom
      if (aMin === -1 && bMin === -1) return 0;
      if (aMin === -1) return 1;
      if (bMin === -1) return -1;

      const aDiff = Math.abs(aMin - currentMinutes);
      const bDiff = Math.abs(bMin - currentMinutes);
      return aDiff - bDiff;
    });

    return rows;
  }, [
    batchesRaw,
    sessionsByBatch,
    effectiveAdmin,
    searchBatch,
    batchTimingFilter,
    scheduleFilter,
  ]);

  /* ── Level 2: sessions for selected batch ───────────────────────────── */
  const selectedBatchRow = useMemo(
    () => batchesRaw.find((b) => b.batch_no === selectedBatchNo),
    [batchesRaw, selectedBatchNo],
  );

  const sessionsForBatch = useMemo(() => {
    if (!selectedBatchNo) return [];
    return (sessionsByBatch.get(selectedBatchNo) || []).sort((a, b) => {
      const da = new Date(a.lastUpdated || a.createdDate || 0).getTime();
      const db = new Date(b.lastUpdated || b.createdDate || 0).getTime();
      return db - da;
    });
  }, [selectedBatchNo, sessionsByBatch]);

  const paginatedSessions = sessionsForBatch.slice(
    (sessionPage - 1) * PAGE_SIZE,
    sessionPage * PAGE_SIZE,
  );
  const totalSessionPages = Math.ceil(sessionsForBatch.length / PAGE_SIZE);

  /* ── report + header counters ────────────────────────────────────────
   * Computed server-side over the ongoing batches (not the filtered table
   * rows, and not the all-time session history) so the numbers stay correct
   * regardless of search/timing/schedule filters:
   *   Batches   = all ongoing batches for this admin
   *   Live      = batches with a session live right now
   *   Completed = sessions that ended in the last 12 hours
   *   Today     = batches that held a session today (IST) so far          */
  const fetchReport = useCallback(async () => {
    if (!effectiveAdmin) return;
    try {
      const res = await api.get("/batch-sessions/report/window", {
        params: { hours: REPORT_WINDOW_HOURS, admin: effectiveAdmin },
      });
      if (!mountedRef.current) return;
      setReport(res.data?.data || null);
      setReportError(false);
    } catch (err) {
      console.error("[BatchSessions] Report fetch error:", err);
      if (mountedRef.current) setReportError(true);
    }
  }, [effectiveAdmin]);

  useEffect(() => {
    setReport(null);
    fetchReport();
    const id = setInterval(() => {
      if (!document.hidden) fetchReport();
    }, REPORT_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchReport]);

  const counter = (v: number | undefined) => (v === undefined ? "–" : v);
  const totalBatches = counter(report?.counters.totalOngoingBatches);
  const liveSessions = counter(report?.counters.liveBatches);
  const completedSessions = counter(report?.counters.completedLastWindow);
  const todaySessions = counter(report?.counters.todayTillNow);

  /* ── drill-down handlers ────────────────────────────────────────────── */
  const handleViewBatch = (batchNo: string) => {
    setSelectedBatchNo(batchNo);
    setViewMode("sessions");
    setSessionPage(1);
  };

  const handleBack = () => {
    setViewMode("batches");
    setSelectedBatchNo(null);
  };

  /* ── clear filters ──────────────────────────────────────────────────── */
  const clearFilters = () => {
    setSearchBatch("");
    setBatchTimingFilter("");
    setScheduleFilter("");
    setAdminFilter(loggedInAdmin);
    setBatchPage(1);
  };

  const hasActiveFilters =
    searchBatch ||
    batchTimingFilter ||
    scheduleFilter ||
    (adminFilter && adminFilter !== loggedInAdmin);

  /* ── page number array ──────────────────────────────────────────────── */
  function pageNumbers(current: number, total: number): (number | "...")[] {
    return Array.from({ length: total }, (_, i) => i + 1)
      .filter((p) => p === 1 || p === total || Math.abs(p - current) <= 1)
      .reduce<(number | "...")[]>((acc, p, idx, arr) => {
        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);
  }

  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* ── TOP HEADER ── */}
      <div className="bg-[#221E45] text-white px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 text-white border border-white/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                Batch Sessions
              </h1>
              <p className="text-xs text-white/60 mt-0.5">
                {effectiveAdmin
                  ? `Viewing: ${effectiveAdmin} — Monitor session participation`
                  : "Monitor session participation"}
              </p>
            </div>
          </div>

          {/* ── STAT CARDS ── */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Total Batches */}
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-white/70"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <div className="text-right">
                <p className="text-sm font-bold text-white leading-none">
                  {totalBatches}
                </p>
                <p className="text-xs text-white/60" title="All ongoing batches">Batches</p>
              </div>
            </div>
            {/* Live */}
            <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 rounded-lg px-3 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-emerald-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-right">
                <p className="text-sm font-bold text-emerald-300 leading-none">
                  {liveSessions}
                </p>
                <p className="text-xs text-emerald-300/70" title="Batches in a live meeting right now">Live</p>
              </div>
            </div>
            {/* Completed */}
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-white/70"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-right">
                <p className="text-sm font-bold text-white leading-none">
                  {completedSessions}
                </p>
                <p className="text-xs text-white/60" title="Sessions completed in the last 12 hours">Completed · 12h</p>
              </div>
            </div>
            {/* Today */}
            <div className="flex items-center gap-2 bg-violet-500/20 border border-violet-400/40 rounded-lg px-3 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-violet-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div className="text-right">
                <p className="text-sm font-bold text-violet-300 leading-none">
                  {todaySessions}
                </p>
                <p className="text-xs text-violet-300/70" title="Batches that held a session today, till now">Today</p>
              </div>
            </div>
          </div>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 border border-white/20"
            title="Refresh"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-4 h-4 text-white ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        {viewMode === "batches" && (
          <AutomationReport report={report} error={reportError} />
        )}

        {/* ── FILTER BAR (Level 1 only) ── */}
        {viewMode === "batches" && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 mb-5">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Label */}
              <div className="flex items-center gap-1.5 mr-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                  />
                </svg>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Filters
                </span>
              </div>

              {/* Batch No search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search Batch No..."
                  value={searchBatch}
                  onChange={(e) => {
                    setSearchBatch(e.target.value);
                    setBatchPage(1);
                  }}
                  className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 w-44 placeholder:text-slate-400 text-slate-700"
                />
                {searchBatch && (
                  <button
                    onClick={() => setSearchBatch("")}
                    className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Batch Timing dropdown */}
              <div className="relative">
                <select
                  value={batchTimingFilter}
                  onChange={(e) => {
                    setBatchTimingFilter(e.target.value);
                    setBatchPage(1);
                  }}
                  className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 appearance-none bg-white text-slate-700 min-w-[160px]"
                >
                  <option value="">All Timings</option>
                  {allTimings.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Schedule Type dropdown */}
              <div className="relative">
                <select
                  value={scheduleFilter}
                  onChange={(e) => {
                    setScheduleFilter(e.target.value);
                    setBatchPage(1);
                  }}
                  className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 appearance-none bg-white text-slate-700 min-w-[160px]"
                >
                  <option value="">All Schedules</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Flexible">Flexible</option>
                  <option value="All Days">All Days</option>
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Admin dropdown */}
              <div className="relative">
                <select
                  value={adminFilter}
                  onChange={(e) => {
                    setAdminFilter(e.target.value);
                    setBatchPage(1);
                  }}
                  className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 appearance-none bg-white text-slate-700 min-w-[160px]"
                >
                  <option value="">All Admins</option>
                  {allAdmins.map((a) => (
                    <option key={a} value={a}>
                      {a} {a === loggedInAdmin ? "(You)" : ""}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Clear All */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear All
                </button>
              )}

              <div className="ml-auto flex items-center gap-4">
                <DownloadReport
                  batches={batchesRaw}
                  sessions={sessions}
                  admins={allAdmins}
                  defaultAdmin={effectiveAdmin}
                  today={getTodayIST()}
                />
                <span className="text-xs text-slate-400">
                  <span className="font-semibold text-slate-600">
                    {batchRows.length}
                  </span>{" "}
                  {batchRows.length === 1 ? "batch" : "batches"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── TABLE CARD ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* ── LEVEL 2 HEADER: back button + batch title ── */}
          {viewMode === "sessions" && selectedBatchNo && (
            <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-[#221E45] bg-[#221E45]/8 hover:bg-[#221E45]/15 border border-[#221E45]/20 rounded-lg transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back to Batches
                </button>
                <div className="h-5 w-px bg-slate-200" />
                <div>
                  <h2 className="text-sm font-bold text-slate-800">
                    Batch #{selectedBatchNo}
                    {selectedBatchRow?.courseName && (
                      <span className="font-normal text-slate-500 ml-1.5">
                        — {selectedBatchRow.courseName}
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {sessionsForBatch.length} session
                    {sessionsForBatch.length !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              {selectedBatchRow?.meetingLink && (
                <a
                  href={selectedBatchRow.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-[#221E45] text-white rounded-lg hover:bg-[#221E45]/90 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Join Meeting
                </a>
              )}
            </div>
          )}

          {/* ── LOADING STATE ── */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-8 h-8 border-[3px] border-[#221E45]/20 border-t-[#221E45] rounded-full animate-spin"></div>
              <p className="text-sm text-slate-400">Loading data...</p>
            </div>
          ) : viewMode === "batches" ? (
            /* ══════════════════ LEVEL 1: BATCHES TABLE ══════════════════ */
            <BatchesTable
              batches={batchRows}
              onViewBatch={handleViewBatch}
              page={batchPage}
              onPageChange={setBatchPage}
            />
          ) : (
            /* ══════════════════ LEVEL 2: SESSIONS TABLE ══════════════════ */
            <>
              {sessionsForBatch.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-2">
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-slate-600">
                    No sessions found
                  </p>
                  <p className="text-sm text-slate-400">
                    No batch sessions recorded for this batch yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Session ID
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Date
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Admin
                        </th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Session Status
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Trainer
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Students
                        </th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Recording
                        </th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedSessions.map((s) => {
                        const trainer = resolveTrainer(s);
                        const st = (s.sessionStatus || "").toLowerCase();
                        const isLive = st === "live" || st === "active";
                        const attendedCount = s.studentDetails.filter((sd) => {
                          const ss = (sd.status || "").toLowerCase();
                          return ss === "joined" || ss === "attended";
                        }).length;

                        return (
                          <tr
                            key={s.sessionId}
                            className={`transition-colors duration-150 ${isLive ? "bg-white hover:bg-emerald-50/20" : "bg-white hover:bg-slate-50/60"}`}
                          >
                            {/* Session ID */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="group relative inline-flex items-center">
                                <button
                                  className="w-6 h-6 rounded-full bg-[#221E45]/20 flex items-center justify-center hover:bg-[#221E45]/30 transition-colors"
                                  title={s.sessionId}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-3.5 h-3.5 text-[#221E45]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  {s.sessionId}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                                </div>
                              </div>
                            </td>

                            {/* Date */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-3 h-3 text-slate-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                {formatDate(s.createdDate)}
                              </div>
                            </td>

                            {/* Admin */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-xs text-slate-700 font-medium">
                                {s.admin || "—"}
                              </span>
                            </td>

                            {/* Session Status */}
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              <SessionStatusPill status={s.sessionStatus} />
                            </td>

                            {/* Trainer */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              {trainer ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-semibold text-slate-800">
                                      {trainer.name}
                                    </span>
                                    <div className="group relative inline-block">
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            trainer.email,
                                          );
                                        }}
                                        className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors"
                                        title={trainer.email}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          strokeWidth={2}
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                          />
                                        </svg>
                                      </button>
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                        {trainer.email}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                                      </div>
                                    </div>
                                  </div>
                                  <StatusPill status={trainer.status} />
                                </div>
                              ) : (
                                <span className="text-slate-300 text-xs">
                                  —
                                </span>
                              )}
                            </td>

                            {/* Students: compact pills with hover */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              {s.studentDetails.length > 0 ? (
                                <div className="group relative inline-block">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 cursor-default">
                                    {attendedCount}/{s.studentDetails.length}
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-3 h-3"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                    </svg>
                                  </span>
                                  {/* Tooltip: name + email + status */}
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 shadow-lg min-w-max">
                                    <p className="font-semibold mb-1 text-slate-300 border-b border-slate-600 pb-1">
                                      Attendance ({attendedCount}/
                                      {s.studentDetails.length})
                                    </p>
                                    {s.studentDetails.map((sd, i) => {
                                      const joined =
                                        (sd.status || "").toLowerCase() ===
                                          "joined" ||
                                        (sd.status || "").toLowerCase() ===
                                          "attended";
                                      return (
                                        <div
                                          key={i}
                                          className="flex items-center gap-2 py-0.5"
                                        >
                                          <span
                                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${joined ? "bg-emerald-400" : "bg-red-400"}`}
                                          />
                                          <span className="font-medium">
                                            {sd.name}
                                          </span>
                                          <span className="text-slate-400">
                                            ({sd.email})
                                          </span>
                                          <span
                                            className={`text-xs ${joined ? "text-emerald-400" : "text-red-400"}`}
                                          >
                                            {sd.status}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {/* Unknown Participants */}
                                    {(s.unknownParticipants?.length ?? 0) > 0 && (
                                      <>
                                        <p className="font-semibold mt-2 mb-1 text-amber-400 border-b border-slate-600 pb-1 flex items-center gap-1">
                                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                                          Unknown ({s.unknownParticipants!.length})
                                        </p>
                                        {s.unknownParticipants!.map((u: any, ui: number) => (
                                          <div key={ui} className="flex items-center gap-2 py-0.5">
                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-400" />
                                            <span className="font-medium">{u.displayName}</span>
                                            <span className="text-amber-400 text-xs">
                                              Joined · {Math.round((u.durationSec || 0) / 60)}m
                                            </span>
                                          </div>
                                        ))}
                                      </>
                                    )}
                                    <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-800"></div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-300 text-xs">
                                  —
                                </span>
                              )}
                            </td>

                            {/* Recording */}
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              <RecordingIndicator
                                status={s.recordingStatus}
                                recordings={s.recordings}
                              />
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => setDetailModal(s)}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 transition-colors"
                                  title="View Details"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </button>
                                {s.meetingLink ? (
                                  <a
                                    href={s.meetingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#221E45] hover:bg-[#221E45]/90 text-white transition-colors"
                                    title="Join Meeting"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                      />
                                    </svg>
                                  </a>
                                ) : (
                                  <span className="text-slate-300 text-xs">
                                    —
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sessions Pagination */}
              {!loading && sessionsForBatch.length > 0 && (
                <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    Showing{" "}
                    <span className="font-semibold text-slate-700">
                      {(sessionPage - 1) * PAGE_SIZE + 1}–
                      {Math.min(
                        sessionPage * PAGE_SIZE,
                        sessionsForBatch.length,
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-slate-700">
                      {sessionsForBatch.length}
                    </span>{" "}
                    sessions
                  </p>
                  {totalSessionPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() =>
                          setSessionPage((p) => Math.max(1, p - 1))
                        }
                        disabled={sessionPage === 1}
                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      {pageNumbers(sessionPage, totalSessionPages).map(
                        (p, idx) =>
                          p === "..." ? (
                            <span
                              key={`e-${idx}`}
                              className="w-8 h-8 flex items-center justify-center text-xs text-slate-400"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setSessionPage(p as number)}
                              className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors ${
                                sessionPage === p
                                  ? "bg-[#221E45] text-white border-[#221E45]"
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                              }`}
                            >
                              {p}
                            </button>
                          ),
                      )}
                      <button
                        onClick={() =>
                          setSessionPage((p) =>
                            Math.min(totalSessionPages, p + 1),
                          )
                        }
                        disabled={sessionPage === totalSessionPages}
                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════════ SESSION DETAIL MODAL ══════════════════ */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pt-8">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDetailModal(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-10rem)] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-[#221E45]/5 to-[#221E45]/10 flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#221E45]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-[#221E45]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-bold text-slate-800 truncate">
                      Session Details
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {detailModal.batchNo} · {detailModal.courseName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDetailModal(null)}
                  className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-slate-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Session meta */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 font-medium mb-0.5">
                    Session ID
                  </p>
                  <p className="text-sm font-mono text-slate-700 break-all">
                    {detailModal.sessionId}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 font-medium mb-0.5">
                    Date
                  </p>
                  <p className="text-sm text-slate-700">
                    {formatDate(detailModal.createdDate)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 font-medium mb-0.5">
                    Admin
                  </p>
                  <p className="text-sm text-slate-700 font-medium">
                    {detailModal.admin}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 font-medium mb-0.5">
                    Session Status
                  </p>
                  <SessionStatusPill status={detailModal.sessionStatus} />
                </div>
              </div>

              {/* Trainer */}
              {(() => {
                const trainer = resolveTrainer(detailModal);
                return trainer ? (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Trainer
                    </p>
                    <div className="space-y-2 bg-slate-50 rounded-xl p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-800">
                          {trainer.name}
                        </span>
                        <button
                          onClick={() =>
                            navigator.clipboard.writeText(trainer.email)
                          }
                          className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors"
                          title={trainer.email}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                        <span className="text-xs text-slate-400">
                          {trainer.email}
                        </span>
                      </div>
                      <StatusPill status={trainer.status} />
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Students */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Students ({detailModal.studentDetails.length})
                </p>
                {detailModal.studentDetails.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">
                    No students enrolled
                  </p>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                    {detailModal.studentDetails.map((st, i) => {
                      const joined =
                        (st.status || "").toLowerCase() === "joined" ||
                        (st.status || "").toLowerCase() === "attended";
                      return (
                        <div
                          key={i}
                          className="px-4 py-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                joined
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-red-50 text-red-600"
                              }`}
                            >
                              {st.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800">
                                {st.name}
                              </p>
                              <p className="text-xs text-slate-400">
                                {st.email}
                              </p>
                            </div>
                            <StatusPill status={st.status || "pending"} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Unknown Participants */}
              {(detailModal.unknownParticipants?.length ?? 0) > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Unknown Participants ({detailModal.unknownParticipants!.length})
                  </p>
                  <div className="divide-y divide-amber-100 border border-amber-200 rounded-xl overflow-hidden bg-amber-50/30">
                    {detailModal.unknownParticipants!.map((u: any, i: number) => (
                      <div
                        key={i}
                        className="px-4 py-4 hover:bg-amber-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-amber-100 text-amber-700">
                            {(u.displayName || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">
                              {u.displayName}
                            </p>
                            <p className="text-xs text-slate-400">
                              {u.userType === "signedin" ? "Google signed-in" : u.userType || "Unknown"}
                              {u.durationSec ? ` · ${Math.round(u.durationSec / 60)}m` : ""}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Joined
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Meeting Link */}
              {detailModal.meetingLink && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Meeting Link
                  </p>
                  <a
                    href={detailModal.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-3 bg-[#221E45] text-white rounded-xl text-sm font-semibold hover:bg-[#221E45]/90 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    Join Meeting
                  </a>
                </div>
              )}

              {/* Recording Info */}
              {detailModal.recordingStatus &&
                detailModal.recordingStatus !== "none" && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Recording
                    </p>
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <RecordingIndicator
                          status={detailModal.recordingStatus}
                          recordings={detailModal.recordings}
                        />
                        {detailModal.recordingCount > 0 && (
                          <span className="text-xs text-slate-500">
                            {detailModal.recordingCount} recording
                            {detailModal.recordingCount > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {detailModal.recordings &&
                        detailModal.recordings.length > 0 && (
                          <div className="space-y-2">
                            {detailModal.recordings.map(
                              (rec: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200"
                                >
                                  <div className="space-y-0.5">
                                    <p className="text-xs font-medium text-slate-700">
                                      Recording {idx + 1}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      {rec.startTime
                                        ? new Date(
                                            rec.startTime,
                                          ).toLocaleTimeString("en-IN", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "—"}
                                      {rec.endTime
                                        ? ` → ${new Date(rec.endTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
                                        : " → In progress"}
                                      {rec.durationSec
                                        ? ` (${Math.round(rec.durationSec / 60)} min)`
                                        : ""}
                                    </p>
                                  </div>
                                  {rec.driveViewLink && (
                                    <a
                                      href={rec.driveViewLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-200"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                        />
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      View
                                    </a>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
      {/* ── Recording Toast Notifications ── */}
      <RecordingToastContainer events={recToasts} onDismiss={dismissToast} />
    </div>
  );
}
